import { useState } from "react";

import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";

import { computeOrderTotals } from "@/features/shared";
import {
  CenteredLoading,
  TabLabelWithCount,
  useSnackbar,
} from "@/features/ui";
import { DownPaymentAllocationsSection } from "./down-payment-allocations-section";
import { ExpensesSection } from "./expenses-section";
import { GeneralInformationSection } from "./general-information-section";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import { ProductVariantsSection } from "./product-variants-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./sales-invoice-form-constants";
import { SelectSalesOrderProductVariantSection } from "./select-sales-order-product-variant-section";

// Maps a top-level form field to the tab index where it is edited, so an
// invalid submit can jump straight to the offending tab. Required fields that
// live outside the tabs (buyer, paymentTerm, transactionDate) are always
// visible and intentionally omitted.
const FIELD_TO_TAB = {
  salesInvoiceProductVariants: 0,
  salesInvoiceExpenses: 1,
  salesDownPaymentAllocations: 2,
};

const TABS = [
  { label: "Faktur", field: "salesInvoiceProductVariants" },
  { label: "Biaya Lain-lain", field: "salesInvoiceExpenses" },
  { label: "Uang Muka", field: "salesDownPaymentAllocations" },
];

export const SalesInvoiceForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
}) => {
  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
  } = methods;
  const { showSnackbar } = useSnackbar();

  const [tabIndex, setTabIndex] = useState(0);

  // Live row counts shown on each tab.
  const tabRows = useWatch({ control, name: TABS.map(({ field }) => field) });

  const onFormSubmit = async (formData) => {
    const allocations = formData.salesDownPaymentAllocations;
    const totalAllocation = allocations.reduce(
      (sum, a) => sum + (Number(a.amount) || 0),
      0,
    );
    if (totalAllocation > formData.grandTotal) {
      const message = "Total uang muka tidak boleh melebihi Grand Total";
      setError("salesDownPaymentAllocations", { type: "max", message });
      setTabIndex(FIELD_TO_TAB.salesDownPaymentAllocations);
      showSnackbar(message, "error");
      return;
    }

    const additionalCharges = (formData.salesInvoiceExpenses || []).reduce(
      (sum, expense) => sum + (expense.amount || 0),
      0
    );
    const { taxAmount, grandTotal } = computeOrderTotals({
      ...formData,
      additionalCharges,
    });

    const payload = {
      taxable: formData.taxable,
      totalIncludesTax: formData.taxable && formData.totalIncludesTax,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      sellerId: formData.seller.id,
      buyerableId: formData.buyer.id,
      buyerableType: formData.buyerableType,
      paymentTermId: formData.paymentTerm.id,
      note: formData.note || null,
      taxAmount,
      taxPercentage: formData.taxPercentage,
      discountType: formData.discountType,
      discountAmount: formData.discountInput || 0,
      grandTotal,
      salesInvoiceProductVariants: formData.salesInvoiceProductVariants.map(
        (variant) => ({
          ...(variant.isNewlyAdded
            ? { salesOrderProductVariantId: variant.salesOrderProductVariantId }
            : { id: variant.id }),
          quantity: variant.quantity,
          discount: variant.discount || 0,
          price: variant.price || 0,
          note: variant.note || null,
        }),
      ),
      // The expense id keeps the existing line on the backend (mandatory once
      // a return exists against it); rows added in-form have no id yet.
      salesInvoiceExpenses: formData.salesInvoiceExpenses.map((expense) => ({
        ...(expense.id ? { id: expense.id } : {}),
        outletChartOfAccountId: expense.outletChartOfAccount.id,
        amount: expense.amount,
        note: expense.note || null,
      })),
      salesDownPaymentAllocations: allocations.map((allocation) => ({
        ...(allocation.isNewlyAdded ? {} : { id: allocation.id }),
        sourceSalesInvoiceId: allocation.sourceSalesInvoiceId,
        amount: allocation.amount,
      })),
    };

    await onSubmit(payload);
  };

  // Schema validation runs over the full values object even when a tab is not
  // mounted, so an error can land on a hidden tab. Jump to the first offending
  // tab so the user can actually see and fix it.
  const onInvalid = (formErrors) => {
    const erroredTab = TABS.findIndex(({ field }) => formErrors[field]);
    if (erroredTab !== -1) {
      setTabIndex(erroredTab);
    }
    showSnackbar("Periksa kembali isian yang belum valid", "error");
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit, onInvalid)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Tabs
              value={tabIndex}
              onChange={(_, newValue) => setTabIndex(newValue)}
            >
              {TABS.map(({ label, field }, index) => (
                <Tab
                  key={field}
                  label={
                    <TabLabelWithCount
                      label={label}
                      count={tabRows?.[index]?.length ?? 0}
                    />
                  }
                  sx={{
                    color: errors[field] ? "error.main" : undefined,
                  }}
                />
              ))}
            </Tabs>
            <Divider sx={{ mb: 2.5 }} />

            {tabIndex === 0 && (
              <Stack spacing={2.5}>
                <SelectSalesOrderProductVariantSection />
                <Divider />
                <Typography variant="h6">Faktur Penjualan</Typography>
                <ProductVariantsSection editMode={editMode} />
              </Stack>
            )}

            {tabIndex === 1 && <ExpensesSection />}

            {tabIndex === 2 && <DownPaymentAllocationsSection />}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5, lg: 7 }}>
          <OtherSection />
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 5 }}>
          <PriceSummarySection />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

SalesInvoiceForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
};
